import { DeployEnv, VIDMUSE_DEPLOY_ENV } from "@/common/env";
import { VidMuseEvent } from "@/common/events";
import { logger } from "@/common/logger";
import { configStore } from "@/store/configStore";

const DEFAULT_PROD_VIDEO_DOMAINS = ["https://vidmuse-video.sandcdn.com"];

const DEFAULT_PROD_IMAGE_DOMAINS = ["https://vidmuse.sandcdn.com"];

const DEFAULT_DEV_VIDEO_DOMAINS = ["https://vidmuse-dev-video.sandcdn.com"];

const DEFAULT_DEV_IMAGE_DOMAINS = ["https://vidmuse-dev.sandcdn.com"];

const DEFAULT_STAGING_VIDEO_DOMAINS = [
  "https://vidmuse-staging-video.sandcdn.com",
];

const DEFAULT_STAGING_IMAGE_DOMAINS = ["https://vidmuse-staging.sandcdn.com"];

// Default to the first domain in the list
let activeProdVideoDomain = DEFAULT_PROD_VIDEO_DOMAINS[0];
let activeProdImageDomain = DEFAULT_PROD_IMAGE_DOMAINS[0];
let activeDevVideoDomain = DEFAULT_DEV_VIDEO_DOMAINS[0];
let activeDevImageDomain = DEFAULT_DEV_IMAGE_DOMAINS[0];
let activeStagingVideoDomain = DEFAULT_STAGING_VIDEO_DOMAINS[0];
let activeStagingImageDomain = DEFAULT_STAGING_IMAGE_DOMAINS[0];

async function probeOk(base: string, timeoutMs = 1200): Promise<string> {
  const url = new URL(
    "/static/.well-known/download-probe.txt",
    base,
  ).toString();
  const ac = new AbortController();
  const t = setTimeout(() => ac.abort(), timeoutMs);

  try {
    const res = await fetch(url, {
      method: "GET",
      cache: "no-store",
      mode: "cors",
      signal: ac.signal,
    });
    if (!res.ok) throw new Error(`http_${res.status}`);
    return base;
  } catch (error) {
    console.error(`Probe failed for ${base}`, error);
    logger.event(VidMuseEvent.DomainProbeFailed, {
      domain: base,
      error: String(error),
    });
    throw error;
  } finally {
    clearTimeout(t);
  }
}

async function pickFirstReachable(bases: string[]): Promise<string> {
  try {
    // Promise.any: 第一个 fulfilled 返回；全部失败才 reject
    return await Promise.any(bases.map((b) => probeOk(b)));
  } catch (error) {
    // Falls back to the first domain if all fail
    console.error("All domains failed probe, falling back to default.", error);
    return bases[0];
  }
}

export async function initDomainProbe() {
  const env = VIDMUSE_DEPLOY_ENV;
  const { video, image } = configStore.cdnConfig;

  if (env === DeployEnv.Prod) {
    const domainsVideo =
      video.prod.length > 0 ? video.prod : DEFAULT_PROD_VIDEO_DOMAINS;
    const domainsImage =
      image.prod.length > 0 ? image.prod : DEFAULT_PROD_IMAGE_DOMAINS;

    const [prodVideo, prodImage] = await Promise.all([
      pickFirstReachable(domainsVideo),
      pickFirstReachable(domainsImage),
    ]);
    activeProdVideoDomain = prodVideo;
    activeProdImageDomain = prodImage;
  } else if (env === DeployEnv.Staging) {
    const domainsVideo =
      video.staging.length > 0 ? video.staging : DEFAULT_STAGING_VIDEO_DOMAINS;
    const domainsImage =
      image.staging.length > 0 ? image.staging : DEFAULT_STAGING_IMAGE_DOMAINS;

    const [stagingVideo, stagingImage] = await Promise.all([
      pickFirstReachable(domainsVideo),
      pickFirstReachable(domainsImage),
    ]);
    activeStagingVideoDomain = stagingVideo;
    activeStagingImageDomain = stagingImage;
  } else if (env === DeployEnv.Dev || env === DeployEnv.Local) {
    const domainsVideo =
      video.dev.length > 0 ? video.dev : DEFAULT_DEV_VIDEO_DOMAINS;
    const domainsImage =
      image.dev.length > 0 ? image.dev : DEFAULT_DEV_IMAGE_DOMAINS;

    const [devVideo, devImage] = await Promise.all([
      pickFirstReachable(domainsVideo),
      pickFirstReachable(domainsImage),
    ]);
    activeDevVideoDomain = devVideo;
    activeDevImageDomain = devImage;
  }
}

export function getProdVideoDomain() {
  return activeProdVideoDomain;
}

export function getProdImageDomain() {
  return activeProdImageDomain;
}

export function getDevVideoDomain() {
  return activeDevVideoDomain;
}

export function getDevImageDomain() {
  return activeDevImageDomain;
}

export function getStagingVideoDomain() {
  return activeStagingVideoDomain;
}

export function getStagingImageDomain() {
  return activeStagingImageDomain;
}

export function getVideoDomain() {
  return activeProdVideoDomain;
}

export function getImageDomain() {
  return activeProdImageDomain;
}
